import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { colors } from '../../style/rulesEngine';
import useGreenScrollbar from './useGreenScrollbar';

const ReactDOM = (() => {
  try { return require('react-dom'); } catch (_) { return null; }
})();

const LocationTypeahead = ({
  options = [],
  selectedValue,
  onChange,
  placeholder = 'Select…',
  testID,
  error = false,
}) => {
  const initialDisplay = useMemo(() => {
    if (!selectedValue) return '';
    const match = options.find((o) => o.value === selectedValue);
    return match ? match.label : String(selectedValue);
  }, [selectedValue, options]);

  const [text, setText] = useState(initialDisplay);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 240 });
  const triggerRef = useRef(null);
  const interactingRef = useRef(false);
  const scrollTestID = testID ? `${testID}_scroll` : 'locationTypeaheadScroll';
  useGreenScrollbar(scrollTestID);

  useEffect(() => { setText(initialDisplay); }, [initialDisplay]);

  const filtered = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const label = (o.label || '').toLowerCase();
      const value = (o.value || '').toLowerCase();
      return label.includes(q) || value.includes(q);
    });
  }, [text, options]);

  const panelVisible = open && filtered.length > 0;

  const measureTrigger = () => {
    const node = triggerRef.current;
    if (!node || typeof node.getBoundingClientRect !== 'function') return;
    const rect = node.getBoundingClientRect();
    setPos({
      top:   rect.bottom + 4,
      left:  rect.left,
      width: Math.max(rect.width, 240),
    });
  };

  useEffect(() => {
    if (!panelVisible || typeof window === 'undefined') return undefined;
    const onMove = () => measureTrigger();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [panelVisible]);

  const commit = (raw) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) {
      onChange('');
      return;
    }
    const exact = options.find(
      (o) => o.label.toLowerCase() === trimmed.toLowerCase()
              || o.value.toLowerCase() === trimmed.toLowerCase()
    );
    onChange(exact ? exact.value : trimmed);
  };

  const handlePick = (option) => {
    setText(option.label);
    onChange(option.value);
    setOpen(false);
    interactingRef.current = false;
  };

  const handleChangeText = (next) => {
    setText(next);
    if (!open) {
      measureTrigger();
      setOpen(true);
    }
  };

  const handleFocus = () => {
    measureTrigger();
    setOpen(true);
  };

  const handleBlur = () => {
    if (interactingRef.current) return;
    setOpen(false);
    commit(text);
  };

  const panel = panelVisible ? (
    <View
      testID={testID ? `${testID}_panel` : 'locationTypeaheadPanel'}
      onMouseDown={(e) => e.preventDefault()}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: pos.width,
        zIndex: 9999,
        borderRadius: 6,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.12)',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.4,
        shadowRadius: 8,
        elevation: 20,
      }}
    >
      <LinearGradient colors={['#132131', '#1b374f']}>
        <ScrollView
          testID={scrollTestID}
          style={{ maxHeight: 240 }}
          bounces={false}
          showsVerticalScrollIndicator={true}
          keyboardShouldPersistTaps="handled"
        >
          {filtered.map((option) => {
            const isSelected = option.value === selectedValue;
            return (
              <TouchableOpacity
                key={option.value}
                onPressIn={() => { interactingRef.current = true; }}
                onPress={() => handlePick(option)}
                testID={testID ? `${testID}_option_${option.value}` : `locationTypeaheadOption_${option.value}`}
                style={{
                  paddingVertical: 10,
                  paddingHorizontal: 12,
                  backgroundColor: isSelected ? 'rgba(72,90,114,0.5)' : 'transparent',
                  borderBottomWidth: 1,
                  borderBottomColor: 'rgba(255,255,255,0.06)',
                }}
              >
                <Text
                  style={{
                    color: isSelected ? colors.accent : colors.text,
                    fontFamily: 'Poppins_500Medium',
                    fontSize: 13,
                  }}
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </LinearGradient>
    </View>
  ) : null;

  return (
    <View ref={triggerRef} collapsable={false}>
      <TextInput
        value={text}
        onChangeText={handleChangeText}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        placeholderTextColor={colors.textMuted}
        style={[typeaheadStyles.input, error && typeaheadStyles.inputError]}
        testID={testID}
      />
      {panel && ReactDOM && typeof document !== 'undefined'
        ? ReactDOM.createPortal(panel, document.getElementById('root') || document.body)
        : panel}
    </View>
  );
};

const typeaheadStyles = {
  input: {
    backgroundColor: 'transparent',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.25)',
    color: colors.text,
    fontSize: 15,
    paddingVertical: 8,
    paddingHorizontal: 0,
    fontFamily: 'Poppins_500Medium',
  },
  inputError: {
    borderBottomColor: colors.danger,
  },
};

export default LocationTypeahead;
